/**
 * The provider-neutral auth seam (US-008, made runtime in US-011).
 *
 * Everything in the SPA that needs an identity — `AuthContext`, `api.ts` —
 * goes through this module and never imports a vendor adapter directly.
 *
 * <h2>Which adapter answers, and when that is decided</h2>
 *
 * Three sources, consulted in this order, every one of them failing safe to
 * keycloak:
 *
 *   1. `login_provider` from `GET /api/config/public`, adopted into
 *      `runtimeAuthConfig`. THE ANSWER — but NOT NECESSARILY BEFORE THE FIRST
 *      DISPATCH. `AuthContext.boot()` fires that fetch without awaiting it and
 *      awaits only when completing a callback the Scalekit adapter started,
 *      because an unconditional await put an untimed round trip in front of
 *      authentication on every page load (5f4946622). On a cold load the adopt
 *      can therefore land AFTER `startSession()` has already dispatched on (2);
 *      `boot()` re-dispatches onto whichever adapter this answer names, and
 *      `scalekit.ts`'s manager signature rebuilds a client made from (2).
 *      A LOGIN DISPATCH IS THE EXCEPTION, because a full-page navigation has no
 *      second pass: `AuthContext`'s `commitLoginRedirect` waits — bounded — for
 *      this answer before calling `beginLogin` / `beginFederatedLogin`, and
 *      falls through to (2) only once that bound expires.
 *   2. `VITE_AUTH_PROVIDER`, the build-time flag. The BOOT FALLBACK only —
 *      what answers in the window before (1) lands, and inside a renewal iframe
 *      that has not fetched it.
 *   3. `DEFAULT_AUTH_PROVIDER` = keycloak. The ultimate fail-safe: an
 *      unrecognised value at either layer lands here rather than locking
 *      anybody out.
 *
 * (1) is why this story could make the switch independently reversible. A
 * build-time flag is not: rolling the backend back left a served bundle still
 * talking to the wrong issuer until a new build shipped. With the answer coming
 * from the same response `auth_provider` already came from, flipping
 * `DASHBOARD_SCALEKIT_LOGIN_ENABLED` and reloading is the whole switch, in both
 * directions.
 *
 * <h2>The ordering requirement, which did not go away</h2>
 *
 * The backend that serves `login_provider` must be live BEFORE any deployment
 * advertises Scalekit — the SPA cannot adopt a field an older backend does not
 * send, and would fall back to its build-time flag. That is now a much smaller
 * hazard than it was (the fallback is keycloak, and keycloak keeps working
 * because US-007 leaves its dashboard tenant accepting tokens), but it is still
 * the order to deploy in. `useAuthProviderName.ts` states it too.
 *
 * Nothing here is memoised at module load: the flag and the snapshot are read
 * on every dispatch, so a test can drive both bindings in one file and a live
 * SPA picks up an adopted config without a reload.
 */

import * as keycloakAdapter from './keycloak';
import * as scalekitAdapter from './scalekit';
import { isRecoveryRedirectPending, isRecoveryTerminal } from './browserSession';
import {
  DEFAULT_AUTH_PROVIDER,
  getRuntimeAuthConfig,
  normaliseAuthProviderName,
  type AuthProviderName,
} from './runtimeAuthConfig';

// Re-exported so the ~dozen call sites that already import these from here
// keep working. The definitions moved to `runtimeAuthConfig.ts` because
// `api/publicConfig.ts` needs them and importing them from this module would
// drag both vendor SDKs into every bundle that wanted the Stripe key.
export { DEFAULT_AUTH_PROVIDER, normaliseAuthProviderName };
export type { AuthProviderName };

/** Resolve the BUILD-TIME provider flag. The boot fallback, not the answer. */
export const resolveBuildTimeAuthProviderName = (): AuthProviderName =>
  normaliseAuthProviderName(import.meta.env.VITE_AUTH_PROVIDER);

/**
 * Which adapter runs right now: the adopted runtime answer if there is one,
 * otherwise the build-time flag, otherwise keycloak.
 *
 * <p>The runtime layer wins OUTRIGHT rather than merging, including when it
 * says keycloak and the bundle was built for scalekit. That case is the
 * rollback — the backend has gone back to Keycloak and this bundle has not been
 * rebuilt yet — and honouring the build-time flag there would send users to an
 * issuer whose tokens the backend no longer accepts.
 */
export const resolveAuthProviderName = (): AuthProviderName =>
  getRuntimeAuthConfig()?.loginProvider ?? resolveBuildTimeAuthProviderName();

/** Provider-neutral session events the SPA reacts to. */
export type AuthSessionHandlers = {
  /** A session was established (or re-established) outside the boot path. */
  onAuthenticated?: () => void;
  /** The session ended, from either side. */
  onLoggedOut?: () => void;
  /** The access token is expiring; a renewal should be kicked off. */
  onTokenExpired?: () => void;
  /** A mid-flight auth error that warrants restarting the flow. */
  onAuthError?: (err: unknown) => void;
  /** A renewal error that warrants restarting the flow. */
  onRefreshError?: () => void;
};

/**
 * The surface every adapter must supply. Mirrors `AuthProviderPort` in
 * `src/__tests__/authProviderContract.test.ts`, plus the three members the
 * React layer needs (`getSubject`, `subscribeToSessionEvents`) that the
 * offline contract does not exercise.
 */
export type AuthAdapter = {
  describeClient(): {
    clientId: string;
    pkceChallengeMethod: string;
    issuerFor(idpBaseUrl: string): string;
  };
  buildRedirectUri(path?: string): string;
  startSession(): Promise<boolean>;
  isSessionBootstrapped(): boolean;
  beginLogin(redirectPath?: string): Promise<void>;
  beginFederatedLogin(redirectPath?: string): Promise<void>;
  getAccessToken(): Promise<string | null>;
  endSession(): Promise<void>;
  recoverSession(): Promise<never>;
  getSubject(): string | undefined;
  subscribeToSessionEvents(handlers: AuthSessionHandlers): () => void;
};

/**
 * The keycloak-js binding. Every member is a thunk: the vendor functions are
 * reached at CALL time, never captured at module-evaluation time, so a test
 * that mocks `./keycloak` sees its double here too.
 */
const keycloakPort: AuthAdapter = {
  describeClient: () => keycloakAdapter.describeClient(),
  buildRedirectUri: (path) => keycloakAdapter.buildRedirectUri(path),
  startSession: () => keycloakAdapter.initKeycloak(),
  isSessionBootstrapped: () => keycloakAdapter.isKeycloakInitialized(),
  beginLogin: (redirectPath) => keycloakAdapter.loginWithPassword(redirectPath),
  beginFederatedLogin: (redirectPath) => keycloakAdapter.loginWithGoogle(redirectPath),
  getAccessToken: () => keycloakAdapter.ensureFreshToken(),
  endSession: () => keycloakAdapter.logout(),
  recoverSession: () => keycloakAdapter.forceRecovery(),
  getSubject: () => keycloakAdapter.getKeycloak().tokenParsed?.sub,
  subscribeToSessionEvents: (handlers) => {
    const keycloak = keycloakAdapter.getKeycloak();
    keycloak.onAuthSuccess = () => handlers.onAuthenticated?.();
    keycloak.onAuthLogout = () => handlers.onLoggedOut?.();
    keycloak.onTokenExpired = () => handlers.onTokenExpired?.();
    keycloak.onAuthError = (err) => handlers.onAuthError?.(err);
    keycloak.onAuthRefreshError = () => handlers.onRefreshError?.();
    return () => {
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
      keycloak.onAuthError = undefined;
      keycloak.onAuthRefreshError = undefined;
    };
  },
};

/** The Scalekit binding (oidc-client-ts, Authorization Code + PKCE S256). */
const scalekitPort: AuthAdapter = {
  describeClient: () => scalekitAdapter.describeClient(),
  buildRedirectUri: (path) => scalekitAdapter.buildRedirectUri(path),
  startSession: () => scalekitAdapter.startSession(),
  isSessionBootstrapped: () => scalekitAdapter.isSessionBootstrapped(),
  beginLogin: (redirectPath) => scalekitAdapter.beginLogin(redirectPath),
  beginFederatedLogin: (redirectPath) => scalekitAdapter.beginFederatedLogin(redirectPath),
  getAccessToken: () => scalekitAdapter.getAccessToken(),
  endSession: () => scalekitAdapter.endSession(),
  recoverSession: () => scalekitAdapter.recoverSession(),
  getSubject: () => scalekitAdapter.getSubject(),
  subscribeToSessionEvents: (handlers) =>
    scalekitAdapter.subscribeToSessionEvents({
      onAuthenticated: handlers.onAuthenticated,
      onLoggedOut: handlers.onLoggedOut,
      onTokenExpired: handlers.onTokenExpired,
    }),
};

const PORTS: Record<AuthProviderName, AuthAdapter> = {
  keycloak: keycloakPort,
  scalekit: scalekitPort,
};

export const getAuthAdapter = (): AuthAdapter => PORTS[resolveAuthProviderName()];

// ───────────────────────────────────────────────────────────────────────────
// Flat convenience surface
//
// Call sites read better without an adapter handle, and routing through these
// keeps the "which provider" decision at the moment of use rather than at the
// moment of import.
// ───────────────────────────────────────────────────────────────────────────

export const buildRedirectUri = (path?: string): string =>
  getAuthAdapter().buildRedirectUri(path);
export const startSession = (): Promise<boolean> => getAuthAdapter().startSession();
export const isSessionBootstrapped = (): boolean => getAuthAdapter().isSessionBootstrapped();
export const beginLogin = (redirectPath?: string): Promise<void> =>
  getAuthAdapter().beginLogin(redirectPath);
export const beginFederatedLogin = (redirectPath?: string): Promise<void> =>
  getAuthAdapter().beginFederatedLogin(redirectPath);
export const getAccessToken = (): Promise<string | null> => getAuthAdapter().getAccessToken();
export const endSession = (): Promise<void> => getAuthAdapter().endSession();
export const recoverSession = (): Promise<never> => getAuthAdapter().recoverSession();
export const getSubject = (): string | undefined => getAuthAdapter().getSubject();
export const subscribeToSessionEvents = (handlers: AuthSessionHandlers): (() => void) =>
  getAuthAdapter().subscribeToSessionEvents(handlers);

export { isRecoveryRedirectPending, isRecoveryTerminal };
